import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { TextField, useFormContext } from '@/components/form';
import { moneyFieldProps } from '@/components/form/numberInputProps';

interface ManualPricingFieldsProps {
  /** Project currency; only RON exists at creation time, but edit knows better. */
  currency?: string;
}

/**
 * Manual pricing — what the materials cost and what price was offered.
 *
 * For jobs that never get an itemised offer: rather than building a room full
 * of priced items to arrive at a number everyone already agreed on, the two
 * figures are simply recorded. They are kept as their own bordered block
 * because they are the only money on the form, and because the margin beneath
 * them only means anything read as a pair.
 *
 * Both are optional and independent — a project may have a price agreed before
 * anyone has costed the materials, or the reverse.
 */
export function ManualPricingFields({ currency = 'RON' }: ManualPricingFieldsProps) {
  const { t } = useTranslation();
  const form = useFormContext();
  const [materialCost, offeredPrice] = useWatch({
    control: form.control,
    name: ['manualMaterialCost', 'manualOfferedPrice'],
  }) as [string | undefined, string | undefined];

  const margin = marginOf(materialCost, offeredPrice);

  return (
    <fieldset className="space-y-4 rounded-md border border-border p-4">
      <legend className="px-1 text-sm font-medium">{t('projects.manualPricing.title')}</legend>
      <p className="text-xs text-muted-foreground">{t('projects.manualPricing.hint')}</p>

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          name="manualMaterialCost"
          label={t('projects.fields.manualMaterialCost')}
          placeholder="0.00"
          {...moneyFieldProps}
        />
        <TextField
          name="manualOfferedPrice"
          label={t('projects.fields.manualOfferedPrice')}
          placeholder="0.00"
          {...moneyFieldProps}
        />
      </div>

      {/* Only once both figures exist: a margin against a missing cost would be
          the offered price wearing a different label. */}
      {margin !== null ? (
        <p
          className={`text-xs font-medium ${
            margin.amount < 0 ? 'text-destructive' : 'text-muted-foreground'
          }`}
        >
          {t('projects.manualPricing.margin', {
            amount: `${margin.amount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${currency}`,
            percent: margin.percent === null ? '—' : `${margin.percent.toFixed(1)}%`,
          })}
        </p>
      ) : null}
    </fieldset>
  );
}

/**
 * Offered price minus material cost, plus what share of the price that is.
 *
 * Null unless both figures parse, so a half-typed "12." shows nothing rather
 * than flickering a wrong number. The percentage is null when the price is
 * zero — there is no margin to take a share of.
 */
function marginOf(
  materialCost: string | undefined,
  offeredPrice: string | undefined,
): { amount: number; percent: number | null } | null {
  const cost = Number(materialCost);
  const price = Number(offeredPrice);
  if (!materialCost || !offeredPrice || !Number.isFinite(cost) || !Number.isFinite(price)) {
    return null;
  }
  const amount = price - cost;
  return { amount, percent: price === 0 ? null : (amount / price) * 100 };
}
